import type { Project } from '../../data/types';
import { buildIndexHtml, publishFilenameStem } from './htmlTemplate';
import {
  PublishBundleError,
  type BuildPublishBundleInput,
  type BuildPublishBundleResult,
  type PublishFileMap,
} from './types';

/**
 * Top-level publish-bundle pipeline.
 *
 * Pulls the trained model artefacts from the trainer, renders the
 * static `index.html` from the kid's PublishConfig, snapshots the
 * config into `manifest.json`, and packs everything into a zip Blob.
 *
 * M2 supports image-kind sticker-cam end-to-end. Class-quiz and
 * sound-reactor reuse the same bundle layout (labels + emoji
 * mappings) so the index.html runtime can branch on
 * `body[data-template]` and pick the right behaviour. Other project
 * kinds (audio, pose) raise PublishBundleError until their feature
 * extractors are wired into the runtime.
 */

const SUPPORTED_KIND_FOR_BUNDLE: ReadonlyArray<Project['kind']> = ['image', 'pose', 'audio'];

export async function buildPublishFileMap(input: BuildPublishBundleInput): Promise<PublishFileMap> {
  const { project, classes, config, trainer } = input;

  if (!SUPPORTED_KIND_FOR_BUNDLE.includes(project.kind)) {
    throw new PublishBundleError(
      'unsupported-kind',
      `Download isn't available for ${project.kind} projects yet — coming soon.`,
    );
  }
  if (!trainer.extractArtifacts) {
    throw new PublishBundleError('no-trained-model', 'Train your model first, then download.');
  }

  // Pull the trained model artefacts. The trainer reads from TF.js's
  // own IndexedDB store; we get back a parsed model.json + raw
  // weights bytes + sidecar metadata.
  const artifacts = await trainer.extractArtifacts({
    projectId: project.id,
    projectName: project.name,
    // The bundle indexes labels by classId so it can carry over the
    // kid's per-class emoji even if a class is renamed post-train.
    labels: classes.map((c) => ({ classId: c.id, name: c.name })),
  });

  const html = buildIndexHtml({
    project,
    classes,
    config,
    trainedLabels: artifacts.metadataJson.brytaLabels,
  });

  const manifest = {
    schemaVersion: 1 as const,
    project: { id: project.id, name: project.name, kind: project.kind },
    config,
    trainedLabels: artifacts.metadataJson.brytaLabels,
    exportedAt: new Date().toISOString(),
  };

  return {
    files: {
      'index.html': html,
      'model.json': JSON.stringify(artifacts.modelJson),
      'weights.bin': artifacts.weightsBin,
      'manifest.json': JSON.stringify(manifest, null, 2),
      'metadata.json': JSON.stringify(artifacts.metadataJson, null, 2),
      // Friendly text README so a kid (or parent) opening the zip
      // outside a browser sees what's inside.
      'README.txt': renderReadme(project.name),
    },
  };
}

export async function buildPublishBundleBytes(input: BuildPublishBundleInput): Promise<Uint8Array> {
  const fileMap = await buildPublishFileMap(input);
  const { zipSync } = await import('fflate');
  const encoder = new TextEncoder();
  const zipPayload: Record<string, Uint8Array> = {};
  for (const [path, contents] of Object.entries(fileMap.files)) {
    zipPayload[path] = typeof contents === 'string' ? encoder.encode(contents) : contents;
  }
  const zipped = zipSync(zipPayload);
  // Detach from any shared backing store before handing off — keeps
  // downstream Blob round-trips on jsdom honest.
  const sealed = new Uint8Array(zipped.byteLength);
  sealed.set(zipped);
  return sealed;
}

export async function buildPublishBundle(
  input: BuildPublishBundleInput,
): Promise<BuildPublishBundleResult> {
  const bytes = await buildPublishBundleBytes(input);
  const blob = new Blob([bytes as unknown as BlobPart], {
    type: 'application/zip',
  });
  return {
    blob,
    bytes,
    filename: `bryta-${publishFilenameStem(input.project.name)}-app.zip`,
  };
}

/** Convenience wrapper — packs the zip and triggers a browser
 *  download in the same flow. The "Download" button hits this. */
export async function downloadPublishBundle(input: BuildPublishBundleInput): Promise<void> {
  const { blob, filename } = await buildPublishBundle(input);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // setTimeout 0 lets the browser actually start the download before
  // the URL is revoked.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function renderReadme(projectName: string): string {
  return `${projectName}
${'='.repeat(projectName.length)}

This zip is a self-contained mini-app made on Bryta.

To run it:
  1. Unzip the folder.
  2. Open index.html in a modern browser (Chrome, Safari, Edge,
     Firefox). You'll need to allow camera access.

To publish it on a real URL:
  - Drag the unzipped folder onto Netlify Drop, GitHub Pages,
    Cloudflare Pages, or any static host. No build step required.

What's inside:
  - index.html  → the page itself, with the kid's title + theme
  - model.json  → the trained classifier graph
  - weights.bin → the model's learned weights
  - manifest.json → the kid's composer config snapshot
  - metadata.json → labels + project metadata

Made on Bryta — https://bryta.app
`;
}

/** Re-exports for callers that import via the bundle index. */
export { PublishBundleError } from './types';
export type {
  BuildPublishBundleInput,
  BuildPublishBundleResult,
  PublishBundleBlocker,
  PublishFileMap,
} from './types';
